'use client';

import type { TranslationKey } from '@freedraw/i18n';
import { useAppTranslation, useLocale } from '@freedraw/i18n/react';
import type { AppLocale } from '@freedraw/i18n/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowRight, ArrowUp, ArrowUpRight, Lightbulb, MousePointer2, Sparkles, Users, BrainCircuit, Workflow, Network, Shapes, PenTool } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { BrandMark } from '@/components/brand-mark';
import { LandingEditorPreview } from '@/components/landing/landing-editor-preview';
import { DiagramPreview, type DiagramKind } from '@/components/landing/landing-diagrams';
import { DISPLAY_FONT } from '@/components/landing/landing-fonts';
import { useLandingMotion } from '@/components/landing/landing-motion';
import { LandingParallaxShape } from '@/components/landing/landing-parallax-shapes';
import { LandingFaqSection } from '@/components/landing/landing-sections';
import { LandingShowcaseSection } from '@/components/landing/landing-showcase';
import { LandingTabsSection } from '@/components/landing/landing-tabs';
import { LandingUseCasesSection } from '@/components/landing/landing-use-cases';
import { LanguageSwitcher } from '@/components/language-switcher';
import { TERMS_AND_CONDITIONS_PATH, TERMS_OF_USE_PATH } from '@/data/legal/types';
import { BRAND } from '@/theme';

export type LandingPageProps = {
  isSignedIn: boolean;
  userName: string | null;
  ctaHref: string;
  signInHref: string;
};

const INK = BRAND.ink;
const MUTED = '#555D6D';
const ACCENT = BRAND.pinkStrong;
const CANVAS = BRAND.pageBackground;
const DARK = BRAND.ink;

const NAV_LINKS: Array<{ href: string; key: TranslationKey }> = [
  { href: '#use-cases', key: 'landing.v2NavUseCases' },
  { href: '#features', key: 'landing.navFeatures' },
  { href: '#diagrams', key: 'landing.navDiagrams' },
  { href: '#faq', key: 'landing.navFaq' }
];

const BENEFITS = [
  { icon: Sparkles, title: 'landing.v2BenefitAiTitle', description: 'landing.v2BenefitAiDescription' },
  { icon: MousePointer2, title: 'landing.v2BenefitEditTitle', description: 'landing.v2BenefitEditDescription' },
  { icon: Users, title: 'landing.v2BenefitShareTitle', description: 'landing.v2BenefitShareDescription' }
] as const satisfies ReadonlyArray<{ icon: typeof Sparkles; title: TranslationKey; description: TranslationKey }>;

const EXTRA_BENEFITS = [
  { icon: Shapes, title: { en: 'Shapes that speak for you', vi: 'Hình khối theo ý bạn' },
    description: { en: 'Choose from shapes, sticky notes and connectors to make the message clear.', vi: 'Dùng hình khối, giấy ghi chú và đường nối để truyền đạt rõ ràng hơn.' } },
  { icon: Workflow, title: { en: 'Connect the thinking', vi: 'Kết nối mọi ý tưởng' },
    description: { en: 'Build the flow, change its direction and keep every step in sight.', vi: 'Sắp xếp các bước, đổi hướng kết nối và thấy được toàn bộ quy trình.' } },
  { icon: PenTool, title: { en: 'Color outside the lines', vi: 'Tự do với màu sắc' },
    description: { en: 'Pick colors, arrange elements and turn a rough sketch into your own design.', vi: 'Chọn màu, sắp xếp các thành phần và biến bản phác thảo thành thiết kế riêng.' } }
] as const;

const LANGUAGES: Array<{ locale: AppLocale; name: string }> = [
  { locale: 'en', name: 'English' },
  { locale: 'vi', name: 'Tiếng Việt' }
];

function FooterLanguages() {
  const { locale, setLocale } = useLocale();
  return (
    <Stack spacing={1.5} alignItems="flex-start">
      {LANGUAGES.map(({ locale: itemLocale, name }) => (
        <ButtonBase
          key={itemLocale}
          lang={itemLocale}
          aria-pressed={locale === itemLocale}
          onClick={() => setLocale(itemLocale)}
          sx={{ fontFamily: 'inherit', fontSize: 14, fontWeight: locale === itemLocale ? 800 : 500, color: locale === itemLocale ? '#FFE8AD' : '#D3D0D2', '&:hover': { color: '#FFFFFF' }, '&.Mui-focusVisible': { outline: '2px solid #FFE8AD', outlineOffset: 3 } }}
        >
          {name}
        </ButtonBase>
      ))}
    </Stack>
  );
}


type FeatureHighlight = {
  id: string;
  title: { en: string; vi: string };
  description: { en: string; vi: string };
  detail: { en: string; vi: string };
  diagram: DiagramKind;
  icon: typeof Sparkles;
};

const FEATURE_HIGHLIGHTS: FeatureHighlight[] = [
  {
    id: 'online-whiteboard', icon: PenTool, diagram: 'mindmap',
    title: { en: 'Online Whiteboard', vi: 'Bảng trắng trực tuyến' },
    description: { en: 'A flexible canvas for ideas, sketches and planning.', vi: 'Không gian linh hoạt để phác thảo, lên ý tưởng và lập kế hoạch.' },
    detail: { en: 'Bring sticky notes, shapes and connections together on one visual canvas. Keep your thinking organized as ideas evolve.', vi: 'Kết hợp giấy ghi chú, hình khối và đường nối trên cùng một canvas. Sắp xếp ý tưởng rõ ràng khi nội dung phát triển.' }
  },
  {
    id: 'ai-diagram', icon: BrainCircuit, diagram: 'sequence',
    title: { en: 'AI Diagram Maker', vi: 'Tạo sơ đồ bằng AI' },
    description: { en: 'Turn a written idea into an editable visual starting point.', vi: 'Chuyển ý tưởng bằng văn bản thành sơ đồ trực quan có thể chỉnh sửa.' },
    detail: { en: 'Describe the process you want to explain, then refine its shapes, connections and labels on the board.', vi: 'Mô tả quy trình bạn muốn trình bày, sau đó chỉnh sửa các hình khối, đường nối và nhãn ngay trên bảng.' }
  },
  {
    id: 'flowchart', icon: Workflow, diagram: 'flowchart',
    title: { en: 'Flowchart Maker', vi: 'Vẽ lưu đồ trực tuyến' },
    description: { en: 'Make processes and decisions easier to follow.', vi: 'Biến các quy trình và quyết định thành lưu đồ dễ hiểu.' },
    detail: { en: 'Map steps, decisions and paths using diagram shapes and connectors. Adjust the flow as your plan changes.', vi: 'Sắp xếp các bước, nhánh quyết định và hướng đi bằng hình khối và đường nối. Dễ dàng điều chỉnh khi kế hoạch thay đổi.' }
  },
  {
    id: 'mind-map', icon: Network, diagram: 'mindmap',
    title: { en: 'Mind Map Maker', vi: 'Tạo sơ đồ tư duy' },
    description: { en: 'Explore connections and organize complex thoughts.', vi: 'Khám phá mối liên hệ và sắp xếp những ý tưởng phức tạp.' },
    detail: { en: 'Start from a central topic, branch into related concepts and shape a clear picture of your thinking.', vi: 'Bắt đầu từ chủ đề trung tâm, mở rộng các nhánh liên quan và biến suy nghĩ thành bức tranh dễ theo dõi.' }
  },
  {
    id: 'collaboration', icon: Users, diagram: 'kanban',
    title: { en: 'Collaborative Whiteboard', vi: 'Bảng trắng cộng tác' },
    description: { en: 'Plan, review and build ideas with your team.', vi: 'Cùng nhóm lên kế hoạch, thảo luận và phát triển ý tưởng.' },
    detail: { en: 'Work together on a shared visual space to align on tasks, diagrams and next steps.', vi: 'Làm việc chung trên bảng trực quan để thống nhất công việc, sơ đồ và những bước tiếp theo.' }
  },
  {
    id: 'shapes-templates', icon: Shapes, diagram: 'gantt',
    title: { en: 'Shapes & Diagram Templates', vi: 'Hình khối và mẫu sơ đồ' },
    description: { en: 'Get started faster with ready-made visual building blocks.', vi: 'Bắt đầu nhanh hơn với các thành phần trực quan có sẵn.' },
    detail: { en: 'Use diagram shapes and starter layouts to spend more time explaining your ideas and less time formatting.', vi: 'Sử dụng hình khối và bố cục mẫu để tập trung vào nội dung thay vì tốn thời gian căn chỉnh.' }
  }
];


const FEATURE_PREVIEW_LABELS: Record<string, { en: string; vi: string }> = {
  'online-whiteboard': { en: 'Your idea|Sketch|Plan|Discuss|Explore|Organize|Share', vi: 'Ý tưởng|Phác thảo|Kế hoạch|Thảo luận|Khám phá|Sắp xếp|Chia sẻ' },
  'ai-diagram': { en: 'User|DouBoard|AI|Describe idea|Generate|Edit|Share', vi: 'Người dùng|DouBoard|AI|Mô tả|Tạo sơ đồ|Chỉnh sửa|Chia sẻ' },
  flowchart: { en: 'New idea|Ready?|Create|Review|Done|Yes|No', vi: 'Ý tưởng|Sẵn sàng?|Thực hiện|Xem lại|Hoàn tất|Có|Không' },
  'mind-map': { en: 'Project|Goals|Team|Research|Design|Build|Launch', vi: 'Dự án|Mục tiêu|Nhóm|Nghiên cứu|Thiết kế|Phát triển|Ra mắt' },
  collaboration: { en: 'To do|In progress|Done|Research|Design|Review|Publish', vi: 'Cần làm|Đang làm|Hoàn tất|Nghiên cứu|Thiết kế|Đánh giá|Xuất bản' },
  'shapes-templates': { en: 'Plan|Design|Build|Test|Launch|Tasks|Timeline', vi: 'Lập kế hoạch|Thiết kế|Phát triển|Kiểm thử|Ra mắt|Công việc|Tiến độ' }
};

function LandingFeatureHighlights({ ctaHref }: { ctaHref: string }) {
  const { locale } = useLocale();
  const isVietnamese = locale === 'vi';
  const backgrounds = ['#FCFAF4', '#E6DDFF', '#FFF0F5', '#CFF4D2', '#E8EDFF', '#FFF0D8'];
  const accents = ['#E61E73', '#6243B4', '#C91863', '#167654', '#3855B7', '#97591A'];
  const doodles = ['orbit', 'connector', 'squiggle', 'starburst', 'ribbon', 'blob'] as const;

  return (
    <Box component="section" id="feature-highlights" aria-label={isVietnamese ? 'Tính năng DouBoard' : 'DouBoard features'} sx={{ scrollMarginTop: 76, overflow: 'hidden', bgcolor: '#FCFAF4' }}>
      <Box sx={{ bgcolor: '#FFE58F', py: { xs: 9, md: 13 }, position: 'relative', overflow: 'hidden' }}>
        <Box data-parallax="55" aria-hidden="true" sx={{ position: 'absolute', pointerEvents: 'none', right: -105, top: -120, width: { xs: 250, md: 420 }, height: { xs: 250, md: 420 }, opacity: 0.34 }}>
          <LandingParallaxShape variant="ribbon" color="#8C4D19" />
        </Box>
        <Container maxWidth="xl" sx={{ px: { xs: 2.5, sm: 4, lg: 7 }, position: 'relative' }}>
          <Stack data-reveal spacing={2.5} sx={{ maxWidth: 890 }}>
            <Typography sx={{ color: '#833A3B', fontSize: 12, fontWeight: 850, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {isVietnamese ? 'KHÁM PHÁ DOUBOARD' : 'EXPLORE DOUBOARD'}
            </Typography>
            <Typography component="h2" sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 43, sm: 58, md: 85 },
              fontWeight: 700, letterSpacing: '-0.07em', lineHeight: 1.03, color: INK, textWrap: 'balance' }}>
              {isVietnamese ? 'Mọi ý tưởng trên một canvas' : 'Every idea, one canvas'}
            </Typography>
            <Typography sx={{ color: '#3B4252', maxWidth: 690, fontSize: { xs: 16, md: 18 }, lineHeight: 1.7 }}>
              {isVietnamese
                ? 'Từ phác thảo đầu tiên đến sơ đồ chi tiết, DouBoard mang các công cụ trực quan vào cùng một không gian.'
                : 'From your first sketch to a detailed diagram, DouBoard brings visual thinking tools into one space.'}
            </Typography>
          </Stack>
        </Container>
      </Box>
      {FEATURE_HIGHLIGHTS.map((feature, index) => {
        const Icon = feature.icon;
        const language = isVietnamese ? 'vi' : 'en';
        const accent = accents[index] ?? BRAND.pinkStrong;
        return (
          <Box key={feature.id} component="article" id={feature.id} data-parallax-section data-testid={`landing-feature-${feature.id}`}
            sx={{ scrollMarginTop: 88, bgcolor: backgrounds[index], position: 'relative', overflow: 'hidden',
              isolation: 'isolate', py: { xs: 8, md: 13 }, borderBottom: '1px solid rgba(23,26,34,0.10)' }}>
            <Box data-parallax={index % 2 ? '-48' : '52'} aria-hidden="true"
              sx={{ position: 'absolute', width: { xs: 200, md: 390 }, height: { xs: 200, md: 390 },
                right: index % 2 ? 'auto' : -125, left: index % 2 ? -150 : 'auto', top: 18,
                zIndex: -1, pointerEvents: 'none', opacity: 0.32 }}>
              <LandingParallaxShape variant={doodles[index]!} color={accent} />
            </Box>
            <Container maxWidth="xl" sx={{ px: { xs: 2.5, sm: 4, lg: 7 } }}>
              <Box data-reveal sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 4, md: 9 }, alignItems: 'center' }}>
                <Stack spacing={2.4} sx={{ minWidth: 0, order: { xs: 0, md: index % 2 === 0 ? 0 : 1 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.6}>
                    <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid rgba(23,26,34,0.13)', width: 50, height: 50, borderRadius: '15px', display: 'grid', placeItems: 'center', color: accent }}>
                      <Icon size={26} strokeWidth={1.8} />
                    </Box>
                    <Typography sx={{ fontSize: 12, letterSpacing: '0.13em', fontWeight: 800, color: accent }}>
                      {String(index + 1).padStart(2, '0')} — 06
                    </Typography>
                  </Stack>
                  <Typography component="h3" sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 35, md: 55 }, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.055em' }}>
                    {feature.title[language]}
                  </Typography>
                  <Typography sx={{ color: INK, fontSize: { xs: 18, md: 21 }, lineHeight: 1.4, fontWeight: 700 }}>
                    {feature.description[language]}
                  </Typography>
                  <Typography sx={{ color: '#3B4252', fontSize: 15.5, lineHeight: 1.75, maxWidth: 525 }}>
                    {feature.detail[language]}
                  </Typography>
                  <Button component={Link} href={ctaHref} endIcon={<ArrowUpRight size={17} />}
                    sx={{ alignSelf: 'flex-start', mt: 1, color: accent, textTransform: 'none', fontWeight: 800, pl: 0,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                    {isVietnamese ? 'Bắt đầu vẽ' : 'Start drawing'}
                  </Button>
                </Stack>
                <Box role="img" aria-label={feature.title[language]} sx={{ minWidth: 0, order: { xs: 1, md: index % 2 === 0 ? 1 : 0 },
                  p: { xs: 1.2, sm: 2.5, md: 3.5 },
                  border: '1px solid rgba(23,26,34,0.14)', borderRadius: '24px',
                  bgcolor: 'rgba(255,255,255,0.38)', boxShadow: `15px 16px 0 ${accent}1C`,
                  transform: { md: index % 2 === 0 ? 'rotate(-1.4deg)' : 'rotate(1.4deg)' },
                  transition: 'transform 240ms ease', '&:hover': { transform: 'rotate(0deg)' } }}>
                  <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid rgba(23,26,34,0.10)', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 12px 35px rgba(23,26,34,0.10)' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: '1px solid #E8E7E4' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 850 }}>DouBoard</Typography>
                      <Typography sx={{ fontSize: 11, color: MUTED }}>{feature.title[language]}</Typography>
                    </Stack>
                    <Box sx={{ p: { xs: 1, sm: 2 }, aspectRatio: '400 / 260', display: 'flex', alignItems: 'center' }}>
                      <DiagramPreview kind={feature.diagram} labels={FEATURE_PREVIEW_LABELS[feature.id]![language].split('|')} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>
        );
      })}
    </Box>
  );
}

export function LandingPage({ isSignedIn, userName, ctaHref, signInHref }: LandingPageProps) {
  const { t } = useAppTranslation();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useLandingMotion(scrollRef, []);
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const update = () => setShowBackToTop(scroller.scrollTop > 740);
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    return () => scroller.removeEventListener('scroll', update);
  }, []);

  const ctaLabel = isSignedIn ? t('landing.ctaOpenBoard') : t('landing.ctaStart');
  const ctaTestId = isSignedIn ? 'landing-open-board' : 'landing-get-started';
  const detailCards = [
    ...BENEFITS.map(({ icon, title, description }) => ({ icon, title: t(title), description: t(description) })),
    ...EXTRA_BENEFITS.map(({ icon, title, description }) => ({ icon, title: title[locale], description: description[locale] }))
  ];

  return (
    <Box
      ref={scrollRef}
      data-testid="landing"
      sx={{
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
        bgcolor: CANVAS,
        backgroundImage: 'radial-gradient(circle, rgba(23,26,34,0.07) 1px, transparent 1.35px)',
        backgroundSize: '26px 26px',
        color: INK,
        '@media (prefers-reduced-motion: reduce)': { scrollBehavior: 'auto' }
      }}
    >
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          borderBottom: '1px solid rgba(25, 25, 31, 0.065)',
          bgcolor: 'rgba(252, 250, 244, 0.95)',
          backdropFilter: 'blur(18px)'
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ height: { xs: 64, md: 72 } }}>
            <Stack direction="row" alignItems="center" spacing={{ xs: 2, lg: 5 }} sx={{ minWidth: 0 }}>
              <BrandMark />
              <Stack component="nav" aria-label={t('landing.footerProduct')} direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {NAV_LINKS.map(({ href, key }) => (
                  <Button
                    key={href}
                    href={href}
                    color="inherit"
                    size="small"
                    sx={{ px: { md: 1, lg: 1.35 }, fontWeight: 650, fontSize: 13.5, borderRadius: '8px', color: '#394158', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#EAECE5' } }}
                  >
                    {t(key)}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={{ xs: 0.4, sm: 1 }}>
              <LanguageSwitcher variant="flag" />
              {!isSignedIn ? (
                <Button
                  component={Link}
                  href={signInHref}
                  data-testid="landing-sign-in"
                  size="small"
                  sx={{ fontWeight: 700, color: INK, minWidth: 0, px: { xs: 1, sm: 1.5 }, fontSize: { xs: 12.5, sm: 14 }, whiteSpace: 'nowrap' }}
                >
                  {t('landing.navSignIn')}
                </Button>
              ) : null}
              <Button
                component={Link}
                href={ctaHref}
                size="small"
                sx={{
                  display: { xs: isSignedIn ? 'inline-flex' : 'none', sm: 'inline-flex' },
                  height: 38,
                  px: { xs: 1.25, sm: 2 },
                  bgcolor: BRAND.pinkStrongDark,
                  color: '#FFFFFF',
                  borderRadius: '11px',
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: 12.5, sm: 13.5 },
                  '&:hover': { bgcolor: '#A50F4D' }
                }}
              >
                {isSignedIn ? t('landing.navOpenApp') : t('landing.ctaStart')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        component="section"
        aria-labelledby="landing-hero-title"
        sx={{
          position: 'relative',
          isolation: 'isolate',
          overflow: 'hidden',
          backgroundColor: CANVAS,
          backgroundImage: 'radial-gradient(circle, rgba(23,26,34,0.075) 1px, transparent 1.4px), radial-gradient(ellipse 54% 85% at 86% 47%, #FFE4EE 0%, transparent 78%)',
          backgroundSize: '26px 26px, auto',
          pt: { xs: 6, sm: 8, lg: 10 },
          pb: { xs: 6, lg: 9 }
        }}
      >
        <Box data-parallax="-28" aria-hidden="true" sx={{ position: 'absolute', display: { xs: 'none', sm: 'block' }, top: 40, left: -105, width: 230, height: 230, opacity: 0.3, pointerEvents: 'none' }}>
          <LandingParallaxShape variant="starburst" color="#E61E73" />
        </Box>
        <Container maxWidth="xl" sx={{ position: 'relative', px: { xs: 2.5, sm: 4, lg: 6 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.87fr) minmax(0, 1.13fr)' }, alignItems: 'center', gap: { xs: 4, lg: 5 }, minWidth: 0 }}>
            <Stack spacing={0} sx={{ minWidth: 0, position: 'relative', zIndex: 2, alignItems: 'flex-start' }}>
              <Stack data-hero direction="row" alignItems="center" spacing={1} sx={{ borderRadius: '999px', py: 0.9, px: 1.7, bgcolor: '#FFFFFF', border: '1px solid #F3C7DA', boxShadow: '0 5px 20px rgba(194,24,91,0.05)' }}>
                <Sparkles size={16} color={ACCENT} />
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 850, letterSpacing: '0.065em', color: BRAND.pinkStrongDark, textTransform: 'uppercase' }}>{t('landing.v2Eyebrow')}</Typography>
              </Stack>
              <Typography
                data-hero
                id="landing-hero-title"
                component="h1"
                sx={{
                  mt: { xs: 3, md: 4 },
                  fontFamily: DISPLAY_FONT,
                  fontSize: { xs: 'clamp(2.9rem, 10.8vw, 4.6rem)', sm: 'clamp(4.4rem, 9vw, 6.4rem)', lg: 'clamp(4.45rem, 5.8vw, 6.65rem)' },
                  fontWeight: 700,
                  letterSpacing: '-0.072em',
                  lineHeight: 0.99,
                  color: INK,
                  textWrap: 'balance',
                  maxWidth: { xs: '100%', lg: 700 }
                }}
              >
                <Box component="span" sx={{ display: 'block' }}>{t('landing.v2HeroLine1')}</Box>
                <Box component="span" data-testid="landing-hero-pink-headline" sx={{ display: 'block', mt: 0.17, color: ACCENT, fontStyle: 'italic', position: 'relative', zIndex: 0, overflowWrap: 'anywhere' }}>
                  {t('landing.v2HeroLine2')}
                  <Box component="svg" aria-hidden="true" viewBox="0 0 500 28" preserveAspectRatio="none" sx={{ display: 'block', width: { xs: '84%', md: '93%' }, height: { xs: 13, md: 20 }, ml: '3%', mt: 0.1, overflow: 'visible' }}>
                    <path data-hero-underline d="M7 20 C125 3 305 2 489 15" fill="none" stroke={BRAND.pink} strokeWidth="13" strokeLinecap="round" pathLength="100" />
                  </Box>
                </Box>
              </Typography>
              <Typography data-hero sx={{ mt: { xs: 2.6, md: 3.5 }, maxWidth: 550, fontSize: { xs: 16, md: 18 }, lineHeight: 1.65, color: MUTED }}>
                {t('landing.v2HeroDescription')}
              </Typography>
              <Stack data-hero direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 3.7, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  component={Link}
                  href={ctaHref}
                  data-testid={ctaTestId}
                  endIcon={<ArrowUpRight size={19} />}
                  variant="contained"
                  sx={{
                    height: 54, px: 3.3, borderRadius: '13px',
                    bgcolor: BRAND.pinkStrongDark, color: '#FFFFFF', fontSize: 15.5, fontWeight: 850,
                    boxShadow: '0 12px 24px rgba(194,24,91,0.22)',
                    '&:hover': { bgcolor: '#A50F4D', boxShadow: '0 15px 28px rgba(194,24,91,0.27)', transform: 'translateY(-2px)' },
                    transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease'
                  }}
                >
                  {ctaLabel}
                </Button>
                <Button href="#use-cases" endIcon={<ArrowRight size={17} />} sx={{ height: 52, px: 2.2, borderRadius: '13px', border: '1px solid #EADCE1', bgcolor: '#FFFFFF', color: INK, fontSize: 14.5, fontWeight: 800, '&:hover': { bgcolor: '#FFF0F5', borderColor: BRAND.pink } }}>
                  {t('landing.v2Explore')}
                </Button>
              </Stack>
              <Typography data-hero sx={{ mt: 2.25, color: MUTED, fontSize: 12.5, lineHeight: 1.5 }}>
                {isSignedIn && userName ? t('landing.signedInAs', { name: userName }) : t('landing.ctaHint')}
              </Typography>
              <Stack data-hero direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: { xs: 3.5, md: 4.5 } }}>
                {(['landing.v2Study', 'landing.v2Work', 'landing.v2Design', 'landing.v2Developers'] as TranslationKey[]).map((key, index) => (
                  <Box key={key} component="span" sx={{ fontSize: 12, fontWeight: 750, py: 0.75, px: 1.5, color: INK, bgcolor: index === 0 ? '#FFE8AD' : index === 1 ? '#DFF4E8' : index === 2 ? '#F8D4E2' : '#E6E3FA', borderRadius: '7px', transform: index === 0 ? 'rotate(-2deg)' : index === 2 ? 'rotate(2deg)' : 'none' }}>
                    {t(key)}
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Box data-hero sx={{ position: 'relative', minWidth: 0, zIndex: 1, pt: { xs: 4, lg: 8 }, pb: { xs: 7, md: 8 }, px: { xs: 0.7, sm: 3, lg: 1 } }}>
              <Box component="svg" aria-hidden="true" viewBox="0 0 660 540" sx={{ position: 'absolute', pointerEvents: 'none', top: 0, left: '-8%', width: '118%', height: '98%', zIndex: -1, overflow: 'visible' }}>
                <path d="M86 207 C194 24 507 -7 558 146 C636 329 436 525 197 457 C5 406 -34 293 86 207Z" fill="#FFB5D2" opacity="0.85" />
                <path d="M29 318 C121 196 330 130 533 156" fill="none" stroke="#E61E73" strokeWidth="34" opacity="0.45" strokeLinecap="round" />
                <path d="M535 79 l19 -29 m5 36 l30 -15 m-13 37 l32 2" fill="none" stroke={BRAND.pinkStrong} strokeWidth="5" strokeLinecap="round" />
              </Box>
              <Box data-doodle aria-hidden="true" sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', zIndex: 3, top: { sm: -8, lg: 15 }, left: { sm: 12, lg: 0 }, px: 2.5, py: 2.3, width: 146, bgcolor: '#FFF1B9', color: INK, transform: 'rotate(-9deg)', boxShadow: '0 12px 24px rgba(28,27,29,0.13)', borderRadius: '3px' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, fontFamily: DISPLAY_FONT, lineHeight: 1.2 }}>
                  {t('landing.v2Ideas')}
                </Typography>
                <Typography sx={{ mt: 0.7, fontSize: 12.5, color: '#3E3A36' }}>{t('landing.v2Study')}</Typography>
                <Box sx={{ fontSize: 25, color: ACCENT, textAlign: 'right', mt: 0.6 }}>✳</Box>
              </Box>
              <Box sx={{ position: 'relative', zIndex: 2, bgcolor: '#FFFFFF', border: '1px solid #EADDE3', borderRadius: { xs: '17px', sm: '24px' }, p: { xs: 0.7, sm: 1.25 }, boxShadow: '14px 20px 0 rgba(194,24,91,0.12), 0 30px 70px rgba(23,28,40,0.17)', transform: { sm: 'rotate(-2.2deg)' }, transition: 'transform 260ms ease, box-shadow 260ms ease', '&:hover': { transform: { sm: 'rotate(0deg) translateY(-3px)' }, boxShadow: '10px 18px 0 rgba(194,24,91,0.10), 0 34px 75px rgba(23,28,40,0.18)' } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 0.7, sm: 1.5 }, py: 0.6 }}>
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    {['#F7B5CD', '#FFD778', '#93D9BA'].map((color) => (
                      <Box key={color} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
                    ))}
                    <Typography sx={{ ml: 1, fontSize: 11, color: MUTED, fontWeight: 800 }}>DouBoard / Canvas</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.65}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#1DAE81' }} />
                    <Typography sx={{ fontSize: 10.5, fontWeight: 750, color: MUTED }}>{t('landing.v2LivePreview')}</Typography>
                  </Stack>
                </Stack>
                <Box sx={{ overflow: 'hidden', borderRadius: { xs: '10px', sm: '16px' }, border: '1px solid #F2E8EC' }}>
                  <LandingEditorPreview />
                </Box>
              </Box>
              <Box data-doodle aria-hidden="true" sx={{ position: 'absolute', zIndex: 3, bottom: { xs: 0, sm: 4 }, right: { xs: 0, sm: -3, lg: -14 }, width: { xs: 120, sm: 160 }, px: 2.2, py: 2, bgcolor: '#E1F5E9', boxShadow: '0 12px 25px rgba(28,27,29,0.12)', transform: 'rotate(8deg)', borderRadius: '3px' }}>
                <Typography sx={{ color: INK, fontFamily: DISPLAY_FONT, fontSize: { xs: 15, sm: 18 }, fontWeight: 750, lineHeight: 1.15 }}>
                  {t('landing.v2BenefitShareTitle')}
                </Typography>
                <Typography sx={{ color: ACCENT, mt: 1, fontSize: 24 }}>↗ ♡</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box component="section" aria-label={t('landing.navFeatures')} sx={{ bgcolor: DARK, color: '#FFFFFF', py: { xs: 3.3, md: 4.2 }, overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2.5}>
            <Typography sx={{ maxWidth: 590, fontFamily: DISPLAY_FONT, fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.05em', fontWeight: 700, lineHeight: 1.15 }}>
              {t('landing.v2BenefitEditTitle')}
              <Box component="span" sx={{ color: '#F7B5CD' }}> ✳</Box>
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {[
                { key: 'landing.typeFlowchart', color: '#F7B5CD' },
                { key: 'landing.typeMindmap', color: '#FFE8AD' },
                { key: 'landing.v2BenefitAiTitle', color: '#DFF4E8' }
              ].map((tag, index) => (
                <Box key={tag.key} sx={{ px: 1.8, py: 0.9, bgcolor: tag.color, color: INK, fontSize: 12, fontWeight: 800, borderRadius: index === 1 ? '5px 17px 5px 17px' : '99px', transform: index === 0 ? 'rotate(-3deg)' : index === 2 ? 'rotate(3deg)' : 'none' }}>
                  {t(tag.key as TranslationKey)}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <LandingUseCasesSection ctaHref={ctaHref} />

      <Box component="section" id="features" sx={{
        scrollMarginTop: 76, position: 'relative', isolation: 'isolate',
        overflow: 'hidden', bgcolor: DARK, color: '#FFFFFF',
        py: { xs: 10, md: 16 },
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.075) 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }}>
        <Box aria-hidden="true" data-parallax="75" sx={{
          position: 'absolute', zIndex: -1, pointerEvents: 'none', width: { xs: 390, md: 600 }, height: { xs: 390, md: 600 },
          top: -205, right: -230, opacity: 0.42
        }}>
          <LandingParallaxShape variant="squiggle" color="#FFB5D2" />
        </Box>
        <Box aria-hidden="true" data-parallax="-45" sx={{
          position: 'absolute', zIndex: -1, pointerEvents: 'none', width: 360, height: 360,
          bottom: -125, left: -140, opacity: 0.55
        }}>
          <LandingParallaxShape variant="starburst" color="#FFE58F" />
        </Box>
        <Container maxWidth="xl" sx={{ px: { xs: 2.5, sm: 4, lg: 7 }, position: 'relative' }}>
          <Box data-reveal sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 0.5fr' }, gap: { xs: 3, md: 6 }, alignItems: 'end', mb: { xs: 5, md: 9 } }}>
            <Stack spacing={2.5} sx={{ maxWidth: 780 }}>
              <Typography sx={{ color: '#FFE58F', fontSize: 12, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t('landing.v2BenefitsEyebrow')}</Typography>
              <Typography component="h2" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700,
                fontSize: { xs: 39, sm: 56, md: 76 }, letterSpacing: '-0.065em', lineHeight: 1.03, textWrap: 'balance' }}>
                {t('landing.v2BenefitsTitle')}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: '#CBD2DD', lineHeight: 1.75, maxWidth: 415 }}>{t('landing.v2BenefitsDescription')}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 1.5, md: 2.5 } }}>
            {detailCards.map(({ icon: Icon, title, description }, index) => {
              const backgrounds = ['#FFB5D2', '#FCFAF4', '#FFE58F', '#E6DDFF', '#CFF4D2', '#FFFFFF'];
              return (
                <Box key={index} data-testid={`landing-benefit-${index}`} data-reveal
                  sx={{ minWidth: 0, p: { xs: 3, md: 4 }, bgcolor: backgrounds[index], color: DARK,
                    minHeight: { sm: 238, md: 270 }, borderRadius: index === 0 ? '30px 9px 30px 9px' : '16px',
                    border: '1px solid rgba(23,26,34,0.08)', display: 'flex', flexDirection: 'column',
                    transition: 'transform 220ms ease, box-shadow 220ms ease',
                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 19px 40px rgba(0,0,0,0.19)' },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } }
                  }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, md: 5 } }}>
                    <Icon size={28} strokeWidth={1.55} />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, opacity: 0.45, fontVariantNumeric: 'tabular-nums' }}>{String(index + 1).padStart(2, '0')} / 06</Typography>
                  </Stack>
                  <Typography component="h3" sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 23, md: 26 }, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em' }}>{title}</Typography>
                  <Typography sx={{ mt: 1.5, fontSize: 14.5, lineHeight: 1.7, color: '#3B4252' }}>{description}</Typography>
                </Box>
              );
            })}
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: { xs: 5, md: 7 }, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: '#D6DCE4' }}>DouBoard / 06</Typography>
            <Box component="a" href="#feature-highlights" sx={{ color: '#FFE58F', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 750, fontSize: 13.5, '&:hover': { textDecoration: 'underline' } }}>
              {locale === 'vi' ? 'Khám phá thêm' : 'Explore the tools'} <ArrowUpRight size={16} />
            </Box>
          </Stack>
        </Container>
      </Box>

      <LandingFeatureHighlights ctaHref={ctaHref} />

      <LandingShowcaseSection ctaHref={ctaHref} />
      <LandingTabsSection />
      <LandingFaqSection />

      <Box component="section" sx={{ bgcolor: CANVAS, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack data-reveal alignItems="center" spacing={2.2} sx={{ bgcolor: '#FFE58F', border: '2px solid #171A22', borderRadius: { xs: '20px', md: '32px' }, px: { xs: 3, md: 6 }, py: { xs: 7, md: 9 }, textAlign: 'center', boxShadow: '12px 12px 0 #171A22' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Lightbulb size={18} color={ACCENT}/>
              <Typography sx={{ color: ACCENT, fontWeight: 800, fontSize: 12, letterSpacing: '0.08em' }}>DOUBOARD</Typography>
            </Stack>
            <Typography component="h2" sx={{ maxWidth: 720, fontFamily: DISPLAY_FONT, fontSize: { xs: 34, md: 56 }, lineHeight: 1.12, letterSpacing: '-0.04em', fontWeight: 600, textWrap: 'balance' }}>
              {t('landing.v2FinalTitle')}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: { xs: 15.5, md: 18 }, maxWidth: 570, lineHeight: 1.6 }}>{t('landing.v2FinalDescription')}</Typography>
            <Button component={Link} href={ctaHref} endIcon={<ArrowRight size={18}/>} sx={{ mt: 1.5, px: 3.5, height: 50, bgcolor: ACCENT, color: '#FFFFFF', fontWeight: 800, borderRadius: '11px', '&:hover': { bgcolor: '#29324C' } }}>
              {ctaLabel}
            </Button>
          </Stack>
        </Container>
      </Box>

      {showBackToTop ? (
        <Tooltip title={locale === 'vi' ? 'Lên đầu trang' : 'Back to top'} arrow placement="left">
          <IconButton data-testid="landing-back-to-top"
            aria-label={locale === 'vi' ? 'Lên đầu trang' : 'Back to top'}
            onClick={() => scrollRef.current?.scrollTo({
              top: 0,
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
            })}
            sx={{ position: 'fixed', zIndex: 25, bottom: { xs: 24, md: 32 },
              right: { xs: 18, md: 32 }, width: 48, height: 48,
              bgcolor: BRAND.pinkStrongDark, color: '#FFFFFF',
              border: '2px solid #FFFFFF', boxShadow: '0 12px 30px rgba(145,15,70,0.32)',
              '&:hover': { bgcolor: '#A50F4D', transform: 'translateY(-2px)' },
              '&.Mui-focusVisible': { outline: '3px solid #FFE58F', outlineOffset: 3 },
              transition: 'transform 160ms ease, background-color 160ms ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
            <ArrowUp size={21} strokeWidth={2.4} />
          </IconButton>
        </Tooltip>
      ) : null}

      <Box component="footer" sx={{ bgcolor: DARK, color: '#FFFFFF' }}>
        <Container maxWidth="lg" sx={{ pt: { xs: 7, md: 9 }, pb: 4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(0, 2fr) repeat(3, minmax(0, 1fr))' }, gap: { xs: 4, md: 6 } }}>
            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
              <BrandMark tone="light" />
              <Typography sx={{ mt: 2, maxWidth: 330, fontSize: 14, lineHeight: 1.7, color: '#C4CAD7' }}>
                {t('landing.footerTagline')}
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 12, color: '#aaa5ad', fontWeight: 750 }}>{t('landing.footerProduct')}</Typography>
              {NAV_LINKS.map(({ href, key }) => (
                <Box key={href} component="a" href={href} sx={{ fontSize: 14, color: '#E2E5DD', textDecoration: 'none', '&:hover': { color: '#FFE8AD' } }}>{t(key)}</Box>
              ))}
            </Stack>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 12, color: '#aaa5ad', fontWeight: 750 }}>{t('landing.footerLegal')}</Typography>
              <Box component={Link} href={TERMS_OF_USE_PATH} sx={{ fontSize: 14, color: '#E2E5DD', textDecoration: 'none', '&:hover': { color: '#FFE8AD' } }}>{t('auth.termsOfUse')}</Box>
              <Box component={Link} href={TERMS_AND_CONDITIONS_PATH} sx={{ fontSize: 14, color: '#E2E5DD', textDecoration: 'none', '&:hover': { color: '#FFE8AD' } }}>{t('auth.termsAndConditions')}</Box>
            </Stack>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 12, color: '#aaa5ad', fontWeight: 750 }}>{t('landing.footerLanguage')}</Typography>
              <FooterLanguages />
            </Stack>
          </Box>
          <Typography sx={{ mt: { xs: 6, md: 8 }, pt: 3, borderTop: '1px solid rgba(255,255,255,0.14)', color: '#ACA7AD', fontSize: 12.5 }}>
            {t('landing.footer', { year: new Date().getFullYear() })}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
